/**
 * voice-v8 — wann eine gesprochene Nachricht von selbst rausgeht.
 *
 * ## Woher das kommt
 *
 * Martin, 20.09.2026: *„Wenn ich absenden möchte meine Nachricht, könnte
 * irgendwie ein Auslösewort sein, ohne dass ich irgendeinen Button drücken
 * muss, zum Beispiel ‚jetzt senden' … Mit einer Sekunde Pause oder so
 * irgendwie als Sicherung."*
 *
 * ## Warum es ZWEI Wege sind (Ticket 2420, seine Wahl „c")
 *
 * Gemessen an seinen sieben Tagen, 142 PTT-Drücken:
 *
 *     native App   117   82 %   KEIN Live-Text
 *     Browser       25   18 %   Live-Text
 *
 * Ein Auslösewort braucht Text, der schon während des Sprechens dasteht.
 * Nativ gibt es den nicht (app.ts: *„Die native Engine kann nicht live
 * streamen — der Text kommt nach dem Loslassen."*). Ein Wort allein hätte
 * bei vier von fünf seiner Züge nichts getan — ohne Fehlermeldung.
 *
 * Deshalb entscheiden hier zwei Gründe, und beide führen zum selben Ende:
 *
 *   **Wort** — wo Live-Text vorliegt. Mit seiner Sicherung: das Wort zählt
 *   nur, wenn danach nichts mehr kommt.
 *
 *   **Stille** — überall, auch nativ. Der Pegel ist die Quelle, nicht ein
 *   Zeitgeber auf dem Text: der Erkenner liefert verzögert, nicht im Takt
 *   des Sprechens.
 */

import {
  normWort,
  endetAufWort,
  endwortTreffer,
  istZurueckholen,
  PAUSE_VOR_WORT_MS,
  type EndwortTreffer,
} from "../../shared/sende-wort.js";
import {
  AUTO_ZUSAGE_FRAGE_AB_MS,
  STILLE_AUTO_MS,
  STILLE_LAENGSTE_MS,
  STILLE_TEXTRUHE_MIN_MS,
  satzEnde,
} from "../../shared/stille-auto.js";

/** Kürzer als das darf eine Stille-Schwelle nicht sein. */
export const STILLE_MIN_MS = 2000;
/** Und so lange höchstens — darüber wartet man, statt zu sprechen. */
export const STILLE_MAX_MS = STILLE_LAENGSTE_MS;

/**
 * So lange muss nach dem Auslösewort Ruhe sein, damit es zählt.
 *
 * ⚠️ Martins eigene Sicherung. Sagt er „jetzt senden" mitten im Satz und
 *    redet weiter, passiert nichts.
 */
export const WORT_NACHLAUF_MS = 1200;

/**
 * So lange muss nach „Verwerfen" Ruhe sein — im Text UND am Pegel. (Ticket
 * 2674, Martin: *„ca. 2 Sekunden Sicherheitsfrist … löst erst aus, wenn in
 * diesem Zeitfenster keine weitere Information/kein weiterer Ton nachkommt"*)
 *
 * ⚠️ Länger als beim Absenden: dieses Wort wirft das Diktat weg.
 */
export const VERWERFEN_FRIST_MS = 2000;

/**
 * So lange muss nach einem mehrdeutigen Wort („Weiter", „Go"), das nach
 * einer Sprechpause steht, Ruhe sein — im Text UND am Pegel. (26.09.2026
 * abends, Ticket 2659: „… nicht weiter, [Denkpause] weil …" schickte nach
 * 1,2 s mitten im Satz ab.) Dieselbe Frist wie beim Verwerfen.
 */
export const FUER_SICH_RUHE_MS = VERWERFEN_FRIST_MS;

// Ticket 2780: PAUSE_VOR_WORT_MS steht jetzt in shared/sende-wort.ts — der
// Server misst dieselbe Pause an den Zeiten SEINER Erkennung.
export { PAUSE_VOR_WORT_MS };

export interface SendeEinstellung {
  /** Das Wort oder die Wortfolge. Leer = aus. */
  wort?: string | null;
  /** Nach so vielen Millisekunden Stille absenden. 0 = aus, STILLE_AUTO_MS = Auto. */
  stilleMs?: number;
  /**
   * Nur bei „Auto" (Ticket 2763): hat `autoSchritt` (shared/stille-auto.ts)
   * in diesem Takt „senden" gesagt? Die Karte rechnet es aus, weil dazu die
   * Antwort von Jev gehört — die Regel hier bleibt ohne Netz.
   */
  autoSenden?: boolean;
  /**
   * Nur bei „Auto": sagte der Schritt „senden" wegen einer kurzen Zusage („Ja")? Dann genügt dem Text eine kürzere
   * Ruhe (AUTO_ZUSAGE_FRAGE_AB_MS statt STILLE_TEXTRUHE_MIN_MS) — die Zusage nach einer Frage geht sofort hinaus.
   */
  autoZusage?: boolean;
  /**
   * „Feste Stille" nur nach abgeschlossenem Satz (20.10.2026, Regler „Automatisch absenden", Konto 1): die feste
   * Stille schickt erst ab, wenn die Live-Mitschrift auf „?", „." oder „!" endet (`satzEnde`). Ohne Live-Text
   * (nativer Weg) gilt die bloße Stille — dort gibt es keinen Satz, den man prüfen könnte.
   */
  satzende?: boolean;
  /**
   * Ist ein Diktat, das nur aus „zurückholen" oder „doch nicht" besteht,
   * gerade ein Befehl? (26.09.2026 abends, Ticket 2674) Ja, solange etwas
   * zum Zurückholen daliegt — und seit der dritten Runde (26.09. spät) auch
   * kurz danach (verworfen.ts, `befehlGilt`): dann endet es mit „nichts mehr
   * zum Zurückholen", statt als Nachricht an Vespera zu gehen.
   */
  zurueckholen?: boolean;
}

export interface SendeLage {
  /** Was bisher erkannt wurde — leer auf dem nativen Weg. */
  text: string;
  /** Wie lange der Pegel schon still ist. */
  stillMs: number;
  /**
   * Wie lange der erkannte Text schon unverändert ist.
   *
   * ⚠️ Für die Wort-Sicherung. Sie hängt am TEXT, nicht am Pegel: nach dem
   *    Auslösewort soll nichts mehr NACHKOMMEN, und das sieht man am Text.
   */
  textRuhtMs: number;
  /**
   * Kam in DIESER Aufnahme schon einmal Stimme? (23.09.2026)
   *
   * ⚠️ Die Stille zählte ab dem Öffnen — auch wenn noch gar nichts gesagt
   *    war. Seit das Mikro nach Vesperas Gegenfrage von selbst aufgeht,
   *    ist genau das der Normalfall: man hört die Frage und DENKT erst
   *    nach. Mit Martins 3 s wurde die stumme Aufnahme abgeschickt, bevor
   *    er ein Wort gesagt hatte, und der Server warf sie als „zu leise"
   *    weg. „Absenden nach Stille" heißt: nach dem Sprechen.
   *
   * Aus dem PEGEL, nicht aus dem Text — auf dem nativen Weg gibt es
   * während des Sprechens keinen Text (siehe Test „ohne jeden Text").
   */
  gesprochen: boolean;
  /**
   * Lag laut Mitschrift eine Sprechpause vor dem letzten Wort? (Ticket 2659)
   * `davor` ist der normalisierte Text vor dem Endwort. Ohne Angabe gibt es
   * keine — so beim Nachgehörten, das in Schüben kommt.
   */
  pauseVor?: (davor: string) => boolean;
  /**
   * Nur beim Nachgehörten: der Text ist das Endstück der Aufnahme (die
   * letzten 10 s), und davor war schon Ton — der Server sagt es
   * (server/nachhoeren.ts). Dann ist kein Wort „das ganze Diktat", und nur
   * „Senden" löst aus (shared/sende-wort.ts, EndwortOptionen.tonDavor).
   * (26.09.2026 spät, dritte Runde)
   */
  tonDavor?: boolean;
  /**
   * Kommt der Text aus dem Nachhören (Proben am Server, in Schüben) statt aus der Live-Mitschrift? Dann sagt seine
   * Ruhe nichts (Ticket 2697: Proben erfinden Schnipsel, der Pegel entscheidet) — `textRuhtGenug` gilt nicht.
   */
  nachgehoert?: boolean;
  /**
   * Seit wie vielen ms liegt jedes gemessene Pegelbild bei mindestens DAUERLAERM_PEGEL (ohne Lücke)? Fehlt es, gibt
   * es keinen Dauerlärm — der Notausgang (`dauerlaerm`) greift dann nie.
   */
  dauerLautMs?: number;
}

/**
 * Warum die Karte von selbst handelt.
 *
 *   wort       ein Auslösewort am Ende — abschicken
 *   stille     genug Stille nach dem Sprechen — abschicken
 *   verwerfen  „Verwerfen" am Ende, danach Ruhe — wegwerfen (Ticket 2674)
 *   textfeld   „ins Textfeld" am Ende — ins Eingabefeld legen (Ticket 2674)
 *   zurueckholen  nur „zurückholen"/„doch nicht" — das Verworfene zurück
 *              ins Eingabefeld, diese Aufnahme selbst geht weg
 *   unvollstaendig  die Stille wollte senden, der Satz ist aber erkennbar offen
 *              (shared/satz-vollstaendig.ts, 20.10.2026) — NICHT senden, ins
 *              Eingabefeld legen. Entscheidet die Karte (ptt-modal.ts), nie `sendeGrund`.
 */
export type SendeGrund = "wort" | "stille" | "verwerfen" | "textfeld" | "zurueckholen" | "unvollstaendig";

// 23.09.2026, Ticket 2513: normWort und endetAufWort stehen jetzt in
// shared/sende-wort.ts — der Server braucht dieselbe Regel. Weitergereicht,
// damit die bestehenden Importe bleiben.
export { normWort, endetAufWort };

// 26.09.2026 spät (dritte Runde): `ohneWort` stand hier — das Auslösewort
// fiel nur aus der Blase. Die Regel ist jetzt `ohneAusgeloestesEndwort` in
// shared/sende-wort.ts, angewandt am Server auf seine Erkennung.

/**
 * Soll jetzt abgeschickt werden — und warum?
 *
 * ⚠️ Gibt `null` zurück, solange nichts eingestellt ist. Ohne Wort und ohne
 *    Stille-Schwelle ändert sich gar nichts; das ist die Vorgabe.
 */
export function sendeGrund(
  lage: SendeLage,
  e: SendeEinstellung,
): SendeGrund | null {
  /*
   * Tickets 2659/2674: DIE Tabelle „Endwort → Handlung“ (shared/sende-wort.ts).
   *
   * ⚠️ Steht ein Befehl am Ende, der NICHT abschickt, darf auch die Stille
   *    nicht abschicken, solange er wartet: sonst ginge „… Verwerfen" nach
   *    der Stille-Schwelle als Nachricht hinaus, statt zu verschwinden.
   */
  /** Ruhe im Text UND am Pegel. */
  const ruhe = (ms: number): boolean => lage.textRuhtMs >= ms && lage.stillMs >= ms;
  /*
   * 26.09.2026 spät (dritte Runde): „zurückholen"/„doch nicht" nur als
   * GANZES Diktat (nie aus einem Endstück mit Ton davor) und erst nach
   * derselben Ruhe in Text UND Pegel wie „Verwerfen" — vorher reichte die
   * kurze Wort-Ruhe im Text.
   */
  if (e.zurueckholen && !lage.tonDavor && istZurueckholen(lage.text)) {
    return ruhe(FUER_SICH_RUHE_MS) ? "zurueckholen" : null;
  }
  const t = endwortAmEnde(lage, e);
  if (t?.handlung === "verwerfen") {
    return ruhe(VERWERFEN_FRIST_MS) ? "verwerfen" : null;
  }
  if (t?.handlung === "textfeld") {
    return lage.textRuhtMs >= WORT_NACHLAUF_MS ? "textfeld" : null;
  }
  /*
   * 26.09.2026 abends (Ticket 2659): ein mehrdeutiges Wort schickt nur ab,
   * wenn es (a) das ganze Diktat ist — nach der gewohnten Wort-Ruhe — oder
   * (b) nach einer Sprechpause als letztes steht und danach FUER_SICH_RUHE_MS
   * Ruhe folgen. Sonst ist es Inhalt, und die Stille entscheidet wie sonst.
   * „Senden" bleibt, wie es war.
   */
  if (t && (!t.mehrdeutig || t.allein) && lage.textRuhtMs >= WORT_NACHLAUF_MS) return "wort";
  if (t?.mehrdeutig && t.fuerSich && !t.allein && ruhe(FUER_SICH_RUHE_MS)) return "wort";
  return stilleWeg(lage, e) === null ? null : "stille";
}

/**
 * Schickt die Stille ab — und auf welchem Weg? `"notausgang"` nur über Dauerlärm (`dauerlaerm`), damit das Journal es
 * eigens nennt (`[ptt-ende] … notausgang=ja`). EINE Regel für `sendeGrund` und die Journalzeile.
 */
export function stilleWeg(lage: SendeLage, e: SendeEinstellung): "stille" | "notausgang" | null {
  const schwelle = Number(e.stilleMs ?? 0);
  // Ticket 2763: bei „Auto" entscheidet der Schritt aus shared/stille-auto.ts
  // (Obergrenze, Rückfall, Jevs Urteil) — die Wörter in sendeGrund gelten weiter zuerst.
  // 20.10.2026: in jedem Modus erst, wenn die Mitschrift ruht (textRuhtGenug).
  if (schwelle === STILLE_AUTO_MS) {
    if (!lage.gesprochen) return null;
    if (e.autoSenden === true && textRuhtGenug(lage, e.autoZusage === true)) return "stille";
    // Prüfauflage 20.10. (a): hält DAUERLÄRM den Pegel über der Grenze, wächst keine Stille — dann der Notausgang.
    return dauerlaerm(lage, STILLE_NOTAUSGANG_MS) ? "notausgang" : null;
  }
  if (schwelle < STILLE_MIN_MS || schwelle > STILLE_MAX_MS || !lage.gesprochen) return null;
  if (dauerlaerm(lage, Math.max(schwelle, STILLE_NOTAUSGANG_MS))) return "notausgang";
  if (lage.stillMs < schwelle || !textRuhtGenug(lage, false)) return null;
  // Prüfauflage 20.10. (b): ohne Satzzeichen geht „Feste Stille“ nach der doppelten Stille doch hinaus — nie für immer offen.
  const satzOk = e.satzende !== true || satzGeschlossen(lage.text) || lage.stillMs >= 2 * schwelle;
  return satzOk ? "stille" : null;
}

/**
 * Notausgang (Prüfauflage 20.10.): so lange Stille am Pegel hebt den Textruhe-Schutz auf (erfindet die Erkennung im
 * Rauschen dauernd Wörter, bliebe die Karte sonst offen) — und so lange DAUERLÄRM genügt mit ruhender Mitschrift.
 *
 * ⚠️ Nachprüfung 20.10. (pre.3923 herausgenommen): die erste Fassung nahm jede Textruhe ab 8 s ohne Pegel-Stille —
 *    das schickte mitten in leiser Rede, beim Atmen (40 dB) und in der festen Stufe 3 s ab (stille-textruhe.test.ts,
 *    stille-sprachgrenze.test.ts). „Nie mitten in leiser Rede senden“ geht vor dem Hänge-Schutz: Dauerlärm heißt
 *    jetzt, dass der Pegel ohne ein einziges Bild darunter seit `ab` ms mindestens DAUERLAERM_PEGEL hält
 *    (`dauerLautMs`, gemessen in ptt-modal.ts) UND die Mitschrift so lange steht. Rede hat Täler darunter.
 */
/*
 * ⚠️ Restrisiko (ehrlich, Prüfauflage 20.10.): laute Rede OHNE ein Tal unter 0,8 über 8 s, während die Mitschrift
 *    8 s lang denselben Text zeigt (die Erkennung hängt oder liefert nur Rahmen ohne neues Wort), schickt ab. Bei
 *    Martins übersteuerter Rede vom 20.10. lag das untere Zehntel bei 0,29–0,53 — Täler unter 0,8 gab es in jeder
 *    Aufnahme; in den Tests ist der Fall „Rede mit Tälern“ die Gegenprobe. Erkennbar ist eine Fehlauslösung im Journal:
 *    `[ptt-ende] senden … notausgang=ja dauerLautMs=…` (ptt-modal.ts, ruheStand). Rückdrehen: STILLE_NOTAUSGANG_MS
 *    sehr groß setzen.
 * ⚠️ Kommt kein Pegelbild mehr (Seite verdeckt, Bildtakt steht), ist `dauerLautMs` 0 (ptt-modal.ts, PEGEL_LUECKE_MS).
 */
export const STILLE_NOTAUSGANG_MS = 8000;
/** So hoch muss der Pegel in jedem Bild liegen, damit er als Dauerlärm zählt (die Stille-Grenze ist höchstens 0,8). */
export const DAUERLAERM_PEGEL = 0.8;

/** Dauerlärm seit `ab` ms, und die Mitschrift steht so lange? Nur mit Live-Text, nie beim Nachhören. */
function dauerlaerm(lage: SendeLage, ab: number): boolean {
  return (
    lage.text.trim() !== "" &&
    lage.nachgehoert !== true &&
    (lage.dauerLautMs ?? 0) >= ab &&
    lage.textRuhtMs >= ab
  );
}

/**
 * Ruht die Live-Mitschrift lange genug, dass die Stille abschicken darf? (20.10.2026, Martin: „mitten im Satz
 * abgeschnitten“ — Journal: `textRuhtMs=21`, die Erkennung lieferte noch Wörter.) Ein harter Schutz unter jeder
 * Stufe: solange neue Wörter kommen, wird nicht gesendet, gleich was der Pegel sagt. Ohne Text (nativer Weg) gilt er
 * nicht, ebenso beim Nachhören (`nachgehoert`: Proben in Schüben, dort entscheidet der Pegel — Ticket 2697). Eine
 * kurze Zusage („Ja“ nach einer Frage) braucht nur AUTO_ZUSAGE_FRAGE_AB_MS.
 */
export function textRuhtGenug(lage: Pick<SendeLage, "text" | "textRuhtMs" | "nachgehoert">, zusage: boolean): boolean {
  // ⚠️ Prüfauflage A1 (20.10.): KEIN Ausweg über lange Pegel-Stille — leise Rede/Flüstern liegt unter der Grenze, die
  //    Mitschrift bewegt sich: das ist Rede, nicht Rauschen. Gegen Hängen hilft allein der Dauerlärm-Weg (dauerlaerm).
  if (lage.text.trim() === "" || lage.nachgehoert === true) return true;
  return lage.textRuhtMs >= (zusage ? AUTO_ZUSAGE_FRAGE_AB_MS : STILLE_TEXTRUHE_MIN_MS);
}

/** Endet die Mitschrift auf einem abgeschlossenen Satz („?“, „.“, „!“ — `satzEnde`)? Ohne Text: ja (nichts zu prüfen). */
export function satzGeschlossen(text: string): boolean {
  return text.trim() === "" || satzEnde(text) !== "keins";
}

/**
 * Welches Endwort steht gerade am Ende? (Tickets 2659/2674) Für die Karte:
 * den Hinweis, solange „Verwerfen" auf seine Ruhe wartet, und beim Absenden
 * per Wort, ob es für sich stand. Entschieden wird in `sendeGrund`, mit
 * derselben Tabelle.
 */
export function endwortAmEnde(lage: SendeLage, e: SendeEinstellung): EndwortTreffer | null {
  if (!lage.text.trim()) return null;
  return endwortTreffer(lage.text, e.wort, { pause: lage.pauseVor, tonDavor: lage.tonDavor });
}

/**
 * Ein bestätigtes Wort der Live-Erkennung mit seinen Zeitmarken auf der
 * Tonachse (ms, Soniox `start_ms`/`end_ms`). `null`, wo die Erkennung keine
 * nennt. (26.09.2026 spät, dritte Runde)
 */
export interface LiveWort {
  text: string;
  startMs: number | null;
  endMs: number | null;
  /** 03.10.2026 (Fantasiewörter): kleinste Konfidenz der Teilstücke (Soniox `confidence`). */
  konfidenz?: number | null;
  /** 03.10.2026: Beginn und Ende auf der Uhr der Seite (performance.now) — rausch-woerter.ts. */
  uhrStartMs?: number | null;
  uhrEndMs?: number | null;
}

/**
 * Lag laut den ZEITMARKEN der Erkennung eine Sprechpause vor dem Endwort?
 * (26.09.2026 spät, dritte Runde)
 *
 * Die Ankunftszeit der Mitschrift misst das Netz mit: kommt ein Schub
 * verspätet, sieht eine Atempause wie eine Pause aus — und umgekehrt. Die
 * Erkennung sagt selbst, wann jedes Wort begann und endete.
 *
 * `null`, wenn die Marken es nicht sagen können (das Endwort ist noch nicht
 * bestätigt, der Text passt nicht zu den Wörtern, eine Zeit fehlt) — dann
 * gilt wie bisher die Ankunftszeit.
 */
export function pauseAusZeiten(woerter: readonly LiveWort[], davor: string): boolean | null {
  const ziel = davor.split(" ").filter(Boolean).length;
  if (ziel === 0) return null;
  let n = 0;
  let i = 0;
  while (i < woerter.length && n < ziel) {
    n += normWort(woerter[i]?.text ?? "").split(" ").filter(Boolean).length;
    i++;
  }
  // Das Endwort muss schon bestätigt sein, und die Wörter davor genau `davor`.
  if (n !== ziel || i >= woerter.length) return null;
  if (normWort(woerter.slice(0, i).map((w) => w.text).join(" ")) !== davor) return null;
  const vorEnde = woerter[i - 1]?.endMs ?? null;
  const wortStart = woerter[i]?.startMs ?? null;
  if (vorEnde === null || wortStart === null) return null;
  return wortStart - vorEnde >= PAUSE_VOR_WORT_MS;
}

/**
 * Der Verlauf der Mitschrift in EINER Aufnahme — woran die Karte eine
 * Sprechpause vor dem letzten Wort erkennt. (Ticket 2659)
 *
 * 26.09.2026 spät (dritte Runde): zuerst aus den Zeitmarken der Erkennung
 * (`pauseAusZeiten`). Nur wo die nichts sagen, wie bisher aus der
 * Ankunftszeit: jede Änderung des (normalisierten) Texts wird mit ihrer
 * Zeit gemerkt; stand der Text vor dem Endwort mindestens PAUSE_VOR_WORT_MS
 * lang da, bevor das nächste Stück kam, lag dort eine Pause. Kam alles in
 * einem Schub (das Nachgehörte), weiß der Verlauf nichts.
 */
interface TextVerlauf {
  /** `woerter`: die bestätigten Wörter mit Zeitmarken, falls die Erkennung sie liefert. */
  merke(text: string, jetzt: number, woerter?: readonly LiveWort[]): void;
  pauseVor(davor: string): boolean;
}

export function neuerTextVerlauf(): TextVerlauf {
  const eintraege: { norm: string; seit: number }[] = [];
  let zeiten: readonly LiveWort[] = [];
  return {
    merke(text, jetzt, woerter) {
      // Die Marken auch dann, wenn der Text gleich bleibt: ein Wort wird
      // oft erst bestätigt, nachdem es schon (vorläufig) dastand.
      if (woerter) zeiten = woerter;
      const norm = normWort(text);
      if (eintraege.at(-1)?.norm === norm) return;
      eintraege.push({ norm, seit: jetzt });
      if (eintraege.length > 24) eintraege.shift();
    },
    pauseVor(davor) {
      if (!davor) return false;
      const ausZeiten = pauseAusZeiten(zeiten, davor);
      if (ausZeiten !== null) return ausZeiten;
      for (let i = eintraege.length - 2; i >= 0; i--) {
        const e = eintraege[i];
        if (e?.norm !== davor) continue;
        return (eintraege[i + 1]?.seit ?? e.seit) - e.seit >= PAUSE_VOR_WORT_MS;
      }
      return false;
    },
  };
}
